/**
 * Flair Info — chorégraphie des pages « En savoir plus ».
 * - en-tête : titre mot par mot, pastilles en cascade (au chargement)
 * - contenu : découpé en cartes à chaque titre, cartes qui montent à l'entrée dans l'écran
 * - politiques longues : sommaire collant avec repère de lecture (ordinateur)
 * Sans JS (ou si ce fichier échoue), le garde-fou de layout/theme.liquid retire html.fi-js : tout reste lisible.
 */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const enter = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-in');
      enter.unobserve(entry.target);
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });

  // Index de cascade : chaque enfant reçoit --i (délai = --i × pas, défini en CSS)
  const stagger = (elements, cap = 8) => {
    elements.forEach((el, index) => el.style.setProperty('--i', Math.min(index, cap)));
  };

  function initHero() {
    const hero = document.querySelector('[data-fi-hero]');
    if (!hero) return;

    stagger(hero.querySelectorAll('.fi-chip'), 10);

    // l'élément actif reste visible dans la rangée de pastilles défilante (téléphone)
    const active = hero.querySelector('.fi-chip.is-active');
    const track = hero.querySelector('.fi-nav__track');
    if (active && track && track.scrollWidth > track.clientWidth) {
      track.scrollLeft = active.offsetLeft - track.clientWidth / 2 + active.clientWidth / 2;
    }

    requestAnimationFrame(() => hero.classList.add('is-in'));

    const steps = hero.querySelector('[data-fi-steps]');
    if (steps) {
      stagger(steps.querySelectorAll('.fi-step'));
      stagger(hero.querySelectorAll('.fi-delay'));
      enter.observe(steps);
    }
  }

  // Découpe un bloc de texte riche en cartes, une par titre (le niveau le plus haut présent)
  function cardify(rte) {
    if (!rte || rte.dataset.fiDone) return [];
    rte.dataset.fiDone = '1';

    const headingLevel = ['H2', 'H3', 'H4', 'H5', 'H6'].find((tag) => rte.querySelector(':scope > ' + tag));
    const children = Array.from(rte.children);
    if (!children.length) return [];

    const body = document.createElement('div');
    body.className = 'fi-body';
    const cards = [];
    let card = null;

    children.forEach((child) => {
      if (child.tagName === 'HR') { child.remove(); return; }
      // ignore les paragraphes vides laissés par l'éditeur
      if (child.tagName === 'P' && !child.textContent.trim() && !child.querySelector('img, iframe')) { child.remove(); return; }

      if (!card || (headingLevel && child.tagName === headingLevel)) {
        card = document.createElement('section');
        card.className = 'fi-card fi-reveal';
        cards.push(card);
        body.appendChild(card);

        if (headingLevel && child.tagName === headingLevel) {
          child.classList.add('fi-card__head');
          const text = child.textContent.trim();
          card.id = 'fi-' + (text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 48) || cards.length);
          card.dataset.fiTitle = text.replace(/^\d+\.\s*/, '');
        }
      }

      card.appendChild(child);
    });

    rte.appendChild(body);
    stagger(cards, 3);
    cards.forEach((c) => enter.observe(c));
    return cards;
  }

  // Sommaire collant des politiques, avec la section en cours surlignée
  function buildToc(container, cards) {
    const titled = cards.filter((c) => c.dataset.fiTitle);
    if (titled.length < 4) return;

    const toc = document.createElement('nav');
    toc.className = 'fi-toc';
    toc.setAttribute('aria-label', 'Sommaire');
    toc.innerHTML = '<p class="fi-toc__title">Sommaire</p>';

    const links = new Map();
    titled.forEach((c) => {
      const a = document.createElement('a');
      a.href = '#' + c.id;
      a.textContent = c.dataset.fiTitle;
      a.addEventListener('click', (event) => {
        event.preventDefault();
        c.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        history.replaceState(null, '', '#' + c.id);
      });
      toc.appendChild(a);
      links.set(c, a);
    });

    container.classList.add('has-toc');
    container.prepend(toc);

    const spy = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        links.forEach((a) => a.classList.remove('is-current'));
        links.get(entry.target).classList.add('is-current');
      });
    }, { rootMargin: '-30% 0px -60% 0px' });

    titled.forEach((c) => spy.observe(c));
  }

  function initContent() {
    const pageRte = document.querySelector('.fi-page .shopify-section--main-page .page-content.rte');
    if (pageRte && pageRte.children.length) cardify(pageRte);

    const policy = document.querySelector('.fi-page .shopify-policy__container');
    if (policy) {
      const cards = cardify(policy.querySelector('.shopify-policy__body .rte') || policy.querySelector('.shopify-policy__body'));
      buildToc(policy, cards);
    }

    // Questions de la FAQ / « Comment fonctionne Flair » : apparition en cascade par groupe
    const faqItems = document.querySelectorAll('.fi-page .faq__item');
    let index = 0;
    faqItems.forEach((item) => {
      if (item.previousElementSibling && item.previousElementSibling.classList.contains('faq__category')) index = 0;
      item.classList.add('fi-reveal');
      item.style.setProperty('--i', Math.min(index++, 4));
      enter.observe(item);
    });

    const contact = document.querySelector('.fi-page .contact__main');
    if (contact) { contact.classList.add('fi-reveal'); enter.observe(contact); }

    const tracking = document.getElementById('pp-tracking-page-app');
    if (tracking) { tracking.classList.add('fi-reveal'); enter.observe(tracking); }
  }

  function init() {
    initHero();
    initContent();
    window.fiReady = true;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
